import {
  Component,
  computed,
  effect,
  inject,
  model,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import type {
  Notification,
  NotificationPreferences,
} from '@shared/models/notification';
import {
  NotificationStore,
  NotificationActionService,
} from '@tvd/data-access/notifications';
import { DrawerComponent } from '@tvd/design-system/drawer';
import { Icon } from '@tvd/design-system/icon';
import { Button } from '@tvd/design-system/button';
import { Toggle } from '@tvd/design-system/toggle';
import { PushToggle } from '@tvd/ui/push-toggle';
import { RelativeTimePipe } from '../relative-time/relative-time.pipe';

interface NotificationGroup {
  label: string;
  notifications: Notification[];
}

interface PreferenceItem {
  key: keyof NotificationPreferences;
  labelKey: string;
}

const BOOKING_PREFS: PreferenceItem[] = [
  { key: 'booking.created', labelKey: 'notification.pref.booking.created' },
  { key: 'booking.updated', labelKey: 'notification.pref.booking.updated' },
  { key: 'booking.cancelled', labelKey: 'notification.pref.booking.cancelled' },
  { key: 'booking.deleted', labelKey: 'notification.pref.booking.deleted' },
];

const AVAILABILITY_PREFS: PreferenceItem[] = [
  {
    key: 'availability.requested',
    labelKey: 'notification.pref.availability.requested',
  },
  {
    key: 'availability.approved',
    labelKey: 'notification.pref.availability.approved',
  },
  {
    key: 'availability.rejected',
    labelKey: 'notification.pref.availability.rejected',
  },
];

@Component({
  selector: 'tvd-notification-center',
  imports: [
    DrawerComponent,
    Icon,
    Button,
    Toggle,
    PushToggle,
    RelativeTimePipe,
    TranslatePipe,
  ],
  template: `
    <tvd-drawer
      [(isOpen)]="isOpen"
      [title]="
        (store.showSettings()
          ? 'notification.settings.title'
          : 'notification.title'
        ) | translate
      "
      size="medium"
      (closed)="store.closeDrawer()"
    >
      <!-- Header actions -->
      <div class="flex justify-between items-center mb-3">
        <div>
          @if (store.showSettings()) {
            <button
              tvdButton
              kind="transparent"
              (click)="store.toggleSettings()"
            >
              <tvd-icon icon="arrow_back" class="text-base mr-1" />
              {{ 'notification.title' | translate }}
            </button>
          }
        </div>
        <button
          tvdButton
          kind="transparent"
          (click)="store.toggleSettings()"
          [attr.aria-label]="'notification.settings' | translate"
        >
          <tvd-icon
            [icon]="store.showSettings() ? 'close' : 'settings'"
            class="text-base"
          />
        </button>
      </div>

      @if (store.showSettings()) {
        <!-- Settings view -->
        <p class="text-sm text-muted mb-4 px-1">
          {{ 'notification.settings.description' | translate }}
        </p>

        <div class="space-y-5">
          <!-- Push notifications section -->
          <div>
            <p
              class="text-xs font-medium text-muted uppercase tracking-wider px-1 mb-2"
            >
              {{ 'notification.settings.push' | translate }}
            </p>
            <div class="px-1 py-2">
              <tvd-push-toggle />
            </div>
          </div>

          <!-- Bookings section -->
          <div>
            <p
              class="text-xs font-medium text-muted uppercase tracking-wider px-1 mb-2"
            >
              {{ 'notification.settings.bookings' | translate }}
            </p>
            @for (pref of bookingPrefs; track pref.key) {
              <div class="flex items-center justify-between px-1 py-2">
                <span class="text-sm">{{ pref.labelKey | translate }}</span>
                <tvd-toggle
                  [checked]="store.preferences()[pref.key]"
                  (checkedChange)="onPrefToggle(pref.key, $event)"
                />
              </div>
            }
          </div>

          <!-- Availability section -->
          <div>
            <p
              class="text-xs font-medium text-muted uppercase tracking-wider px-1 mb-2"
            >
              {{ 'notification.settings.availability' | translate }}
            </p>
            @for (pref of availabilityPrefs; track pref.key) {
              <div class="flex items-center justify-between px-1 py-2">
                <span class="text-sm">{{ pref.labelKey | translate }}</span>
                <tvd-toggle
                  [checked]="store.preferences()[pref.key]"
                  (checkedChange)="onPrefToggle(pref.key, $event)"
                />
              </div>
            }
          </div>
        </div>
      } @else {
        <!-- Notifications list view -->
        @if (store.loading()) {
          <div class="flex justify-center py-8">
            <div
              class="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full"
            ></div>
          </div>
        } @else if (store.notifications().length === 0) {
          <div
            class="flex flex-col items-center justify-center py-12 text-center"
          >
            <tvd-icon
              icon="notifications_none"
              class="text-4xl text-muted mb-3"
            />
            <p class="text-sm text-muted">
              {{ 'notification.empty' | translate }}
            </p>
          </div>
        } @else {
          @for (group of groups(); track group.label) {
            <div class="mb-4">
              <p
                class="text-xs font-medium text-muted uppercase tracking-wider px-1 mb-2"
              >
                {{ group.label | translate }}
              </p>
              @for (
                notification of group.notifications;
                track notification.id
              ) {
                <button
                  class="w-full text-left px-3 py-3 rounded-lg transition-colors hover:bg-background-100 flex gap-3 items-start cursor-pointer"
                  [class.bg-background-100]="!notification.read"
                  (click)="onNotificationClick(notification)"
                >
                  <div class="mt-1.5 shrink-0">
                    @if (!notification.read) {
                      <div class="w-2 h-2 rounded-full bg-primary"></div>
                    } @else {
                      <div class="w-2 h-2"></div>
                    }
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium truncate">
                      {{
                        notification.title | translate: notification.metadata
                      }}
                    </p>
                    <p class="text-xs text-muted mt-0.5 line-clamp-2">
                      {{ notification.body | translate: notification.metadata }}
                    </p>
                    <p class="text-xs text-muted mt-1">
                      {{ notification.created | relativeTime }}
                    </p>
                  </div>
                </button>
              }
            </div>
          }
        }
      }
    </tvd-drawer>
  `,
})
export class NotificationCenterComponent {
  readonly store = inject(NotificationStore);
  readonly #router = inject(Router);
  readonly #actions = inject(NotificationActionService);
  readonly isOpen = model(false);

  readonly bookingPrefs = BOOKING_PREFS;
  readonly availabilityPrefs = AVAILABILITY_PREFS;

  constructor() {
    effect(() => {
      this.isOpen.set(this.store.drawerOpen());
    });
    effect(() => {
      const open = this.isOpen();
      if (!open && this.store.drawerOpen()) {
        this.store.closeDrawer();
      }
    });
  }

  readonly groups = computed<NotificationGroup[]>(() => {
    const notifications = this.store.notifications();
    if (notifications.length === 0) return [];

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 86400000);

    const todayItems: Notification[] = [];
    const yesterdayItems: Notification[] = [];
    const earlierItems: Notification[] = [];

    for (const n of notifications) {
      const created = new Date(n.created);
      if (created >= today) todayItems.push(n);
      else if (created >= yesterday) yesterdayItems.push(n);
      else earlierItems.push(n);
    }

    const groups: NotificationGroup[] = [];
    if (todayItems.length > 0)
      groups.push({ label: 'notification.today', notifications: todayItems });
    if (yesterdayItems.length > 0)
      groups.push({
        label: 'notification.yesterday',
        notifications: yesterdayItems,
      });
    if (earlierItems.length > 0)
      groups.push({
        label: 'notification.earlier',
        notifications: earlierItems,
      });
    return groups;
  });

  onPrefToggle(key: keyof NotificationPreferences, value: boolean | null) {
    this.store.updatePreference(key, value ?? false);
  }

  onNotificationClick(notification: Notification) {
    if (!notification.read) {
      this.store.markRead(notification.id);
    }
    this.store.closeDrawer();

    const bookingId = notification.metadata?.['bookingId'] as
      | string
      | undefined;
    if (bookingId) {
      this.#actions.openBooking(bookingId);
      return;
    }

    const intakeId = notification.metadata?.['intakeId'] as string | undefined;
    if (intakeId) {
      this.#actions.openIntake(intakeId);
      return;
    }

    if (notification.actionUrl) {
      if (
        notification.actionUrl.startsWith('http://') ||
        notification.actionUrl.startsWith('https://')
      ) {
        window.open(notification.actionUrl, '_blank');
      } else {
        this.#router.navigateByUrl(notification.actionUrl);
      }
    }
  }
}
